import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { parsePhoneNumber, CountryCode, AsYouType } from 'libphonenumber-js';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

// Common countries with their phone codes
const COUNTRIES = [
  { code: 'US' as CountryCode, name: 'United States', flag: '🇺🇸', dialCode: '+1' },
  { code: 'CA' as CountryCode, name: 'Canada', flag: '🇨🇦', dialCode: '+1' },
  { code: 'GB' as CountryCode, name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
  { code: 'AU' as CountryCode, name: 'Australia', flag: '🇦🇺', dialCode: '+61' },
  { code: 'DE' as CountryCode, name: 'Germany', flag: '🇩🇪', dialCode: '+49' },
  { code: 'FR' as CountryCode, name: 'France', flag: '🇫🇷', dialCode: '+33' },
  { code: 'IN' as CountryCode, name: 'India', flag: '🇮🇳', dialCode: '+91' },
  { code: 'CN' as CountryCode, name: 'China', flag: '🇨🇳', dialCode: '+86' },
  { code: 'JP' as CountryCode, name: 'Japan', flag: '🇯🇵', dialCode: '+81' },
  { code: 'BR' as CountryCode, name: 'Brazil', flag: '🇧🇷', dialCode: '+55' },
  { code: 'MX' as CountryCode, name: 'Mexico', flag: '🇲🇽', dialCode: '+52' },
  { code: 'ES' as CountryCode, name: 'Spain', flag: '🇪🇸', dialCode: '+34' },
  { code: 'IT' as CountryCode, name: 'Italy', flag: '🇮🇹', dialCode: '+39' },
  { code: 'NL' as CountryCode, name: 'Netherlands', flag: '🇳🇱', dialCode: '+31' },
  { code: 'SE' as CountryCode, name: 'Sweden', flag: '🇸🇪', dialCode: '+46' },
  { code: 'NO' as CountryCode, name: 'Norway', flag: '🇳🇴', dialCode: '+47' },
  { code: 'DK' as CountryCode, name: 'Denmark', flag: '🇩🇰', dialCode: '+45' },
  { code: 'FI' as CountryCode, name: 'Finland', flag: '🇫🇮', dialCode: '+358' },
  { code: 'PL' as CountryCode, name: 'Poland', flag: '🇵🇱', dialCode: '+48' },
  { code: 'RU' as CountryCode, name: 'Russia', flag: '🇷🇺', dialCode: '+7' },
  { code: 'KR' as CountryCode, name: 'South Korea', flag: '🇰🇷', dialCode: '+82' },
  { code: 'SG' as CountryCode, name: 'Singapore', flag: '🇸🇬', dialCode: '+65' },
  { code: 'HK' as CountryCode, name: 'Hong Kong', flag: '🇭🇰', dialCode: '+852' },
  { code: 'AE' as CountryCode, name: 'UAE', flag: '🇦🇪', dialCode: '+971' },
  { code: 'SA' as CountryCode, name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966' },
  { code: 'ZA' as CountryCode, name: 'South Africa', flag: '🇿🇦', dialCode: '+27' },
];

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder = "Phone",
  className = "",
  disabled = false
}) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>('GB');
  const [displayValue, setDisplayValue] = useState('');
  const isUserTyping = useRef(false);

  useEffect(() => {
    // Only update display if user is not actively typing
    if (!isUserTyping.current) {
      // Parse existing value to determine country and format
      if (value) {
        try {
          const phoneNumber = parsePhoneNumber(value);
          if (phoneNumber) {
            setSelectedCountry(phoneNumber.country || 'GB');
            // Show only the national number (without country code)
            setDisplayValue(phoneNumber.nationalNumber);
          } else if (value.startsWith('+')) {
            // Extract number without country code for display
            const digitsOnly = value.replace(/\D/g, '');
            if (digitsOnly.startsWith('44') && digitsOnly.length > 2) {
              setDisplayValue(digitsOnly.substring(2)); // Remove UK country code
            } else if (digitsOnly.startsWith('1') && digitsOnly.length > 1) {
              setDisplayValue(digitsOnly.substring(1)); // Remove US/CA country code
            } else {
              setDisplayValue(digitsOnly);
            }
          } else {
            setDisplayValue(value);
          }
        } catch (error) {
          // If parsing fails, just show the raw value without + prefix
          setDisplayValue(value.replace(/^\+\d+/, ''));
        }
      } else {
        setDisplayValue('');
      }
    }
  }, [value]);

  const handleCountryChange = (countryCode: string) => {
    const country = countryCode as CountryCode;
    setSelectedCountry(country);
    
    // If there's a phone number, reformat it with the new country
    if (displayValue) {
      const cleanNumber = displayValue.replace(/\D/g, '');
      if (cleanNumber) {
        const dialCode = COUNTRIES.find(c => c.code === country)?.dialCode || '+44';
        const fullNumber = dialCode + cleanNumber;
        onChange(fullNumber);
      }
    }
  };

    const handlePhoneChange = (inputValue: string) => {
    // Mark that user is actively typing
    isUserTyping.current = true;
    
    // Allow user to type digits, spaces, dashes, and parentheses
    const localFormatted = inputValue.replace(/[^\d\s\-\(\)]/g, '');
    
    // Update display value immediately
    setDisplayValue(localFormatted);
    
    // Get clean digits only
    const cleanNumber = localFormatted.replace(/\D/g, '');
    
    if (cleanNumber) {
      // Create the full international number with country code
      const dialCode = COUNTRIES.find(c => c.code === selectedCountry)?.dialCode || '+44';
      const fullNumber = dialCode + cleanNumber;
      onChange(fullNumber);
    } else {
      onChange('');
    }
    
    // Reset typing flag after a short delay
    setTimeout(() => {
      isUserTyping.current = false;
    }, 100);
  };

  const selectedCountryData = COUNTRIES.find(c => c.code === selectedCountry) || COUNTRIES[0];

  return (
    <div className={`flex ${className}`}>
      <Select
        value={selectedCountry}
        onValueChange={handleCountryChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-[100px] rounded-r-none border-r-0 focus:z-10">
          <SelectValue>
            <div className="flex items-center gap-1">
              <span className="text-base">{selectedCountryData.flag}</span>
              <span className="text-xs">{selectedCountryData.dialCode}</span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {COUNTRIES.map((country) => (
            <SelectItem key={country.code} value={country.code}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{country.flag}</span>
                <span className="text-sm">{country.dialCode}</span>
                <span className="text-sm text-muted-foreground">{country.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="tel"
        placeholder={placeholder}
        value={displayValue}
        onChange={(e) => handlePhoneChange(e.target.value)}
        className="rounded-l-none focus:z-10 flex-1"
        disabled={disabled}
      />
    </div>
  );
}; 